import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { ApplicationManagerProvider, useApplicationManager } from "./contexts/ApplicationManagerContext";
import ApplicationLoader from "./components/common/ApplicationLoader";
import { lazyWithRetry } from "./utils/lazyWithRetry";

// Páginas públicas
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import RegisterPageWithLex from "./pages/auth/RegisterPageWithLex";
import EmailVerificationPage from "./pages/auth/EmailVerificationPage";
import LandingPage from "./pages/landing/LandingPage";
import PricingPage from "./pages/landing/PricingPage";
import AboutPage from "./pages/about/AboutPage";
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";
import AccessDeniedPage from "./pages/common/AccessDeniedPage";

// Lazy load das rotas protegidas COM RETRY AUTOMÁTICO
const SuperAdminRoutes = lazyWithRetry(() => import("./routes/SuperAdminRoutes"));
const ProjectWorkspaceRoutes = lazyWithRetry(() => import("./routes/ProjectWorkspaceRoutes"));

// Agent Creation - Full Screen Experience (outside layout)
const CreationWizardPage = lazyWithRetry(() => import("./pages/agents/CreationWizardPage"));

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#00BFA5",
    },
  },
});

// Componente que decide qual aplicação renderizar baseado no estado
const ApplicationRouter: React.FC = () => {
  const { stage, isLoading, error, user, userRoles } = useApplicationManager();

  // Mostrar loader enquanto inicializa ou em caso de erro
  if (stage === 'INITIALIZING' || stage === 'LOADING_PROJECTS' || stage === 'LOADING_PERMISSIONS' || isLoading) {
    return <ApplicationLoader />;
  }

  // Mostrar loader se erro (ApplicationLoader trata isso)
  if (stage === 'ERROR' || error) {
    return <ApplicationLoader />;
  }

  // Se não autenticado, mostrar rotas públicas
  if (stage === 'UNAUTHENTICATED' || !user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/sobre" element={<AboutPage />} />
        <Route path="/precos" element={<PricingPage />} />
        <Route path="/pricing" element={<Navigate to="/precos" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register-lex" element={<RegisterPageWithLex />} />
        <Route path="/register-traditional" element={<RegisterPage />} />
        <Route path="/register-full" element={<RegisterPage />} />
        <Route path="/auth/verify-email" element={<EmailVerificationPage mode="pending" />} />
        <Route path="/auth/verify-email/:token" element={<EmailVerificationPage mode="verify" />} />
        <Route path="/invite" element={<AcceptInvitePage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Se autenticado e ready, mostrar aplicação baseada nos papéis
  if (stage === 'READY') {
    const hasRole = (role: string) => {
      return userRoles.some(ur => ur.papel?.nome === role && ur.ativo);
    };

    // Debug: Verificar papéis do usuário
    console.log('🔍 [APP ROUTER] Verificando papéis do usuário:', {
      userRoles,
      hasSuper: hasRole('super_admin'),
      hasPlatform: hasRole('platform_admin'),
      hasOwner: hasRole('project_owner'),
      hasAdmin: hasRole('project_admin'),
      hasUser: hasRole('project_user'),
      hasViewer: hasRole('project_viewer')
    });

    // Determinar qual aplicação mostrar baseado nos papéis
    if (hasRole('super_admin') || hasRole('platform_admin')) {
      console.log('🎯 [APP ROUTER] Redirecionando para Super Admin Dashboard');
      return (
        <Routes>
          <Route path="/super-admin/*" element={<SuperAdminRoutes />} />
          <Route path="*" element={<Navigate to="/super-admin/dashboard" replace />} />
        </Routes>
      );
    }

    // Usuários de projeto
    if (hasRole('project_owner') || hasRole('project_admin') || hasRole('project_user') || hasRole('project_viewer')) {
      console.log('🎯 [APP ROUTER] Redirecionando para Project Workspace');
      return (
        <Routes>
          {/* Agent Creation - Full Screen (outside layout) */}
          <Route path="/owner/agents/create" element={<CreationWizardPage />} />
          
          {/* Main workspace with layout */}
          <Route path="/owner/*" element={<ProjectWorkspaceRoutes />} />
          <Route path="/projects" element={<Navigate to="/owner/projects" replace />} />
          <Route path="/projects/:projectId/agents" element={<Navigate to="/owner/dashboard" replace />} />
          <Route path="/agents" element={<Navigate to="/owner/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/owner/dashboard" replace />} />
        </Routes>
      );
    }

    // Fallback para usuários sem papéis ou sem projetos
    // Estes usuários podem criar novos projetos
    console.log('🔍 [APP ROUTER] Usuário autenticado mas sem papéis definidos - direcionando para workspace');
    return (
      <Routes>
        <Route path="/owner/*" element={<ProjectWorkspaceRoutes />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="*" element={<Navigate to="/owner/projects" replace />} />
      </Routes>
    );
  }

  // Fallback final
  return <ApplicationLoader />;
};

// Aplicação principal
export default function AppStable() {
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        <Router future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <ErrorBoundary>
            <ApplicationManagerProvider>
              <Suspense fallback={<ApplicationLoader />}>
                <ApplicationRouter />
              </Suspense>
            </ApplicationManagerProvider>
          </ErrorBoundary>
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}